import { useMemo } from 'react';
import { A2_DISEASE_NAME_OPTIONS } from '@/constants/consentForm';
import { useConsentMasterData } from '@/hooks/main/useConsentMasterData';

type MasterItem = { id: number; name: string };

const toIdOptions = (items?: MasterItem[]) =>
  (items ?? []).map(i => ({ label: i.name, value: i.id }));

const toNameOptions = (items?: MasterItem[]) =>
  (items ?? []).map(i => ({ label: i.name, value: i.name }));

const toStringIdOptions = (items?: MasterItem[]) =>
  (items ?? []).map(i => ({ label: i.name, value: String(i.id) }));

export const useConsentMasterOptions = () => {
  const { data: masterData } = useConsentMasterData();

  const incidentDateTypeOptions = useMemo(
    () => toIdOptions(masterData?.incident_date_types),
    [masterData?.incident_date_types]
  );
  const incidentTypeOptions = useMemo(
    () => toIdOptions(masterData?.incident_types),
    [masterData?.incident_types]
  );
  const walkingConditionOptions = useMemo(
    () => toIdOptions(masterData?.walking_conditions),
    [masterData?.walking_conditions]
  );
  const hotTreatmentTypeOptions = useMemo(
    () => toIdOptions(masterData?.hot_treatment_types),
    [masterData?.hot_treatment_types]
  );
  const diseaseNameOptions = useMemo(
    () => toStringIdOptions(masterData?.diseases),
    [masterData?.diseases]
  );
  const diseaseNameById = useMemo(() => {
    const fromMaster = new Map((masterData?.diseases ?? []).map(i => [String(i.id), i.name]));
    if (fromMaster.size > 0) return fromMaster;
    return new Map(A2_DISEASE_NAME_OPTIONS.map(i => [i.value, i.label]));
  }, [masterData?.diseases]);
  const treatmentContentOptions = useMemo(
    () => toNameOptions(masterData?.treatment_contents),
    [masterData?.treatment_contents]
  );
  const symptomMuscleOptions = useMemo(
    () => toStringIdOptions(masterData?.symptom_muscles),
    [masterData?.symptom_muscles]
  );
  const symptomJointOptions = useMemo(
    () => toStringIdOptions(masterData?.symptom_joints),
    [masterData?.symptom_joints]
  );
  const massagePartOptions = useMemo(
    () => toStringIdOptions(masterData?.massages),
    [masterData?.massages]
  );
  const orthomorphiaPartOptions = useMemo(
    () => toStringIdOptions(masterData?.orthomorphias),
    [masterData?.orthomorphias]
  );
  // HIT-11702: ids for every option, その他 included — legacy a2/r2 stored the master id (3),
  // and the rezept forms mark ○ by comparing against 1/2/3.
  const walkingConditionReasonTypeOptions = useMemo(
    () => toStringIdOptions(masterData?.walking_condition_reason_types),
    [masterData?.walking_condition_reason_types]
  );

  return {
    incidentDateTypeOptions,
    incidentTypeOptions,
    walkingConditionOptions,
    hotTreatmentTypeOptions,
    diseaseNameOptions,
    diseaseNameById,
    treatmentContentOptions,
    symptomMuscleOptions,
    symptomJointOptions,
    massagePartOptions,
    orthomorphiaPartOptions,
    walkingConditionReasonTypeOptions,
  };
};
